import type { JurisdictionCode } from '../domain/jurisdiction';
import type { LicenseStatus, V2InspectorRef } from '../domain/inspection';
import { isAppLanguagePreference, type AppLanguagePreference } from '../i18n/locales';
import { useEffect } from 'react';
import { activeAccountId, scopedStorageKey } from '../persistence/accountScope';
import { V2_APP_LANGUAGE_KEY, V2_INSPECTOR_NAME_KEY, V2_INSPECTOR_PROFILE_KEY } from '../persistence/persistenceContract';
import { createStore, useStore } from './createStore';
import { licenseHolderName, licenseStateOf } from '../../supabase/functions/_shared/accountProtocol';
import { boundLicenseStore, cachedBoundLicense } from './boundLicense';

// Small conveniences under the yanwushi-v2 localStorage namespace. Never authority, never required:
// every read falls back to an empty value. The inspector's name and profile are PER ACCOUNT
// (scopedStorageKey — account isolation 2026-09-21); the App Language stays per device.
export function rememberedInspectorName(): string {
  try {
    const key = scopedStorageKey(V2_INSPECTOR_NAME_KEY);
    return key === null ? '' : (window.localStorage.getItem(key) ?? '');
  } catch {
    return '';
  }
}

export function rememberInspectorName(name: string): void {
  try {
    const key = scopedStorageKey(V2_INSPECTOR_NAME_KEY);
    if (key === null) return;
    const trimmed = name.trim();
    if (trimmed.length === 0) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, trimmed);
  } catch {
    // storage refusal must never block field work
  }
}

// ---------- device inspector profile (NY F1 Round 1 — RP-01 / RT-04) ----------
//
// The inspector's own licence facts, snapshotted onto every new inspection. Since 2026-10-19 (Owner
// decision list item 50, accountProtocol.ts L-1 … L-4) they are NOT typed here any more: they are the
// licence the server bound to the account after New York's list confirmed it (src/state/boundLicense.ts),
// and the report's inspector name is the name on that licence. What the inspector edits in the profile
// is the display name and the photo. A delivered report keeps the licence it was produced under.
//
// Migration: an account that only ever had the old name key reads back that name with NO licence —
// null / '', never an invented 'NY'. The old key is kept in sync and is never deleted.
export interface InspectorProfile {
  name: string;
  licensedState: JurisdictionCode | null;
  licenseNumber: string;
  licenseStatus: LicenseStatus | null;
  // Owner 2026-09-14: when the inspector's own photo was last set (bytes in the media store under
  // src/state/inspectorPhoto.ts INSPECTOR_AVATAR_MEDIA_KEY). null = no photo; the avatar shows the initials.
  photoUpdatedAt: number | null;
}

export function emptyInspectorProfile(): InspectorProfile {
  return { name: '', licensedState: null, licenseNumber: '', licenseStatus: null, photoUpdatedAt: null };
}

export function readInspectorProfile(): InspectorProfile {
  const fallbackName = rememberedInspectorName();
  let raw: string | null = null;
  try {
    const key = scopedStorageKey(V2_INSPECTOR_PROFILE_KEY);
    raw = key === null ? null : window.localStorage.getItem(key);
  } catch {
    return { ...emptyInspectorProfile(), ...boundLicenceFields(), name: fallbackName };
  }
  if (raw === null) return { ...emptyInspectorProfile(), ...boundLicenceFields(), name: fallbackName };
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    parsed = null;
  }
  if (typeof parsed !== 'object' || parsed === null) return { ...emptyInspectorProfile(), ...boundLicenceFields(), name: fallbackName };
  const record = parsed as Record<string, unknown>;
  return {
    name: typeof record.name === 'string' && record.name.length > 0 ? record.name : fallbackName,
    // Whatever an earlier build stored as typed licence facts is ignored: the bound licence is the truth.
    ...boundLicenceFields(),
    photoUpdatedAt: typeof record.photoUpdatedAt === 'number' && Number.isFinite(record.photoUpdatedAt) ? record.photoUpdatedAt : null
  };
}

// The licence facts of the account's bound licence: 'licensed' only while it is current (L-6).
export function boundLicenceFields(at: number = Date.now()): Pick<InspectorProfile, 'licensedState' | 'licenseNumber' | 'licenseStatus'> {
  const license = cachedBoundLicense();
  if (!license) return { licensedState: null, licenseNumber: '', licenseStatus: null };
  return { licensedState: 'NY', licenseNumber: license.licenseNumber, licenseStatus: licenseStateOf(license, at) === 'valid' ? 'licensed' : null };
}

export function writeInspectorProfile(profile: InspectorProfile): InspectorProfile {
  // The licence facts are never written from here (they come from the bound licence).
  const next: InspectorProfile = {
    name: profile.name.trim(),
    ...boundLicenceFields(),
    photoUpdatedAt: profile.photoUpdatedAt ?? null
  };
  try {
    const key = scopedStorageKey(V2_INSPECTOR_PROFILE_KEY);
    if (key !== null) window.localStorage.setItem(key, JSON.stringify({ name: next.name, photoUpdatedAt: next.photoUpdatedAt }));
  } catch {
    // storage refusal must never block field work; the caller still gets the value it asked for
  }
  rememberInspectorName(next.name);
  inspectorProfileStore.set(() => ({ profile: next }));
  return next;
}

// Account isolation (2026-09-21). A device updated from a build that kept ONE profile for everybody
// still carries it under the unscoped keys. It is MOVED — once — to the first account that enters
// after the update (the same account that adopts the device's earlier inspections, v2Database.ts
// resolveAccountDatabase), and the unscoped keys are gone from then on: the next inspector to sign in
// on this phone can never read the previous one's name and licence.
export function adoptLegacyInspectorProfile(): void {
  if (activeAccountId() === null) return;
  try {
    for (const base of [V2_INSPECTOR_PROFILE_KEY, V2_INSPECTOR_NAME_KEY]) {
      const legacy = window.localStorage.getItem(base);
      const scoped = scopedStorageKey(base);
      if (legacy === null || scoped === null || scoped === base) continue;
      if (window.localStorage.getItem(scoped) === null) window.localStorage.setItem(scoped, legacy);
      window.localStorage.removeItem(base);
    }
  } catch {
    // storage refusal must never block signing in
  }
}

// The profile as a store, so the avatar in every top bar and the Home greeting follow a change made in
// Settings (name, photo) without a reload. localStorage stays the source of truth: the store is a
// cache that every write refreshes and every mount re-syncs.
export const inspectorProfileStore = createStore<{ profile: InspectorProfile | null }>({ profile: null });

function sameProfile(a: InspectorProfile, b: InspectorProfile): boolean {
  return a.name === b.name && a.licensedState === b.licensedState && a.licenseNumber === b.licenseNumber && a.licenseStatus === b.licenseStatus && a.photoUpdatedAt === b.photoUpdatedAt;
}

export function currentInspectorProfile(): InspectorProfile {
  const fresh = readInspectorProfile();
  const cached = inspectorProfileStore.get().profile;
  if (cached && sameProfile(cached, fresh)) return cached;
  inspectorProfileStore.set(() => ({ profile: fresh }));
  return fresh;
}

export function resetInspectorProfileStoreForTests(): void {
  inspectorProfileStore.set(() => ({ profile: null }));
}

export function useInspectorProfile(): InspectorProfile {
  const cached = useStore(inspectorProfileStore, (s) => s.profile);
  // The licence facts come from the bound licence, which the server answers AFTER sign-in (accountStore):
  // re-read the profile when it arrives, so Home stops saying "bind your licence" without a reload.
  const bound = useStore(boundLicenseStore, (s) => s.license);
  useEffect(() => {
    currentInspectorProfile();
  }, [bound]);
  return cached ?? readInspectorProfile();
}

// What a NEW inspection records about its inspector: the licence holder's name and licence when a licence
// is bound (L-4); otherwise (a Demo by an unlicensed inspector) the name from the creation form. Frozen
// onto the record.
export function inspectorRefFrom(profile: InspectorProfile, name: string): V2InspectorRef {
  // L-4: with a bound licence the report names the licence holder — never a name typed per inspection.
  const license = cachedBoundLicense();
  return {
    name: license ? licenseHolderName(license) : name.trim().length > 0 ? name.trim() : profile.name,
    licensedState: profile.licensedState,
    licenseNumber: profile.licenseNumber,
    licenseStatus: profile.licenseStatus
  };
}

// App Language preference (Final Implementation Authority §21.1; Owner task book 2026-09-04 §3):
// "system" (follow the device language), zh-CN or en-US — persisted per device once the inspector
// chooses in Settings, so the choice survives every launch. Unknown / corrupt values read as "no
// preference" (= follow the system).
export function readAppLanguage(): AppLanguagePreference | null {
  try {
    const raw = window.localStorage.getItem(V2_APP_LANGUAGE_KEY);
    return isAppLanguagePreference(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeAppLanguage(preference: AppLanguagePreference): void {
  try {
    window.localStorage.setItem(V2_APP_LANGUAGE_KEY, preference);
  } catch {
    // storage refusal must never block field work; the in-memory locale still switches
  }
}
